import { useEffect, useState } from "react";
import { ArrowDownLeft, ArrowUpRight, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { openPrintWindow, reportHtml } from "../../lib/printDoc";
import { inputCls } from "../Login";

function loadRazorpay() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = () => reject(new Error("Could not load payment gateway"));
    document.body.appendChild(s);
  });
}

export default function Wallet() {
  const [wallet, setWallet] = useState(null);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const topup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/wallet/razorpay-order", { amount: parseFloat(amount) });
      await loadRazorpay();
      const rzp = new window.Razorpay({
        key: res.data.key_id,
        amount: res.data.amount,
        currency: "INR",
        order_id: res.data.order_id,
        name: "INDIAFAST Global Couriers",
        description: "Wallet Top-up",
        theme: { color: "#FF5A1F" },
        handler: async (resp) => {
          try {
            const v = await api.post("/wallet/razorpay-verify", {
              order_id: resp.razorpay_order_id,
              payment_id: resp.razorpay_payment_id,
              signature: resp.razorpay_signature,
            });
            toast.success(v.data.message);
            setAmount("");
            load();
          } catch (err) {
            toast.error(apiError(err));
          }
        },
        modal: { ondismiss: () => toast.info("Payment cancelled") },
      });
      rzp.open();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!wallet) return <p className="py-20 text-center text-slate-400">Loading wallet…</p>;

  const filteredTxns = wallet.transactions.filter((t) => filter === "all" || t.type === filter);

  const exportCsv = () => {
    const rows = [["Date", "Type", "Amount (INR)", "Reason", "Order ID"]];
    filteredTxns.forEach((t) => rows.push([
      new Date(t.created_at).toLocaleString("en-IN"), t.type, t.amount,
      (t.reason || "").replaceAll(",", ";"), t.order_id || ""]));
    const csv = rows.map((r) => r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `indiafast-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Transaction report downloaded");
  };

  const exportPdf = () => {
    const rows = filteredTxns.map((t) => [
      t.type.toUpperCase(), `₹${t.amount.toLocaleString("en-IN")}`,
      t.reason || "—", new Date(t.created_at).toLocaleString("en-IN"), t.order_id || "—"]);
    const credits = filteredTxns.filter((t) => t.type === "credit").reduce((s, t) => s + t.amount, 0);
    const debits = filteredTxns.filter((t) => t.type === "debit").reduce((s, t) => s + t.amount, 0);
    openPrintWindow("Wallet Transaction Report",
      reportHtml("Wallet Transaction Report", `Balance: ₹${wallet.balance.toLocaleString("en-IN")} · ${filteredTxns.length} transactions`,
        ["Type", "Amount", "Reason", "Date", "Order ID"], rows,
        ["Totals", `+₹${credits.toLocaleString("en-IN")} / −₹${debits.toLocaleString("en-IN")}`, "", "", ""]));
  };

  return (
    <div data-testid="wallet-page" className="space-y-6">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl">Wallet & Billing</h1>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="relative overflow-hidden rounded-2xl bg-navy p-7 text-white grain">
          <div className="absolute -right-10 -top-10 h-36 w-36 rounded-full bg-saffron/30 blur-2xl" />
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Available Balance</p>
          <p data-testid="wallet-page-balance" className="mt-2 font-heading text-4xl font-extrabold">₹{wallet.balance.toLocaleString("en-IN")}</p>
          <p className="mt-2 text-xs text-slate-400">Prepaid freight is auto-deducted per shipment. COD is settled to this wallet.</p>
        </div>
        <form onSubmit={topup} className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2">
          <h3 className="font-heading text-lg font-bold text-navy">Add Money</h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {[500, 1000, 2000, 5000].map((v) => (
              <button type="button" key={v} onClick={() => setAmount(String(v))} data-testid={`topup-preset-${v}`}
                      className="rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-navy hover:border-saffron hover:text-saffron">₹{v}</button>
            ))}
          </div>
          <div className="mt-4 flex gap-3">
            <input type="number" min="500" required data-testid="topup-amount-input" className={`${inputCls} max-w-xs`}
                   placeholder="Amount (min ₹500)" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <button type="submit" disabled={loading} data-testid="topup-submit-btn"
                    className="flex items-center gap-2 rounded-lg bg-saffron px-6 py-2.5 text-sm font-bold text-white hover:bg-saffron-hover disabled:opacity-60">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Top Up
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-400">Secured by Razorpay — UPI, cards & netbanking accepted. Payment success credits your wallet instantly.</p>
        </form>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <h3 className="font-heading text-lg font-bold text-navy">Transaction Report</h3>
          <div className="flex gap-2">
            <select data-testid="txn-type-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold outline-none focus:border-saffron">
              <option value="all">All Transactions</option>
              <option value="credit">Credits Only</option>
              <option value="debit">Debits Only</option>
            </select>
            <button onClick={exportCsv} data-testid="wallet-report-download"
                    className="flex items-center gap-1.5 rounded-lg bg-navy px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-saffron">
              <Download className="h-3.5 w-3.5" /> Download CSV
            </button>
            <button onClick={exportPdf} data-testid="wallet-report-pdf"
                    className="flex items-center gap-1.5 rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-saffron-hover">
              <Download className="h-3.5 w-3.5" /> Download PDF (A4)
            </button>
          </div>
        </div>
        <table className="w-full text-sm" data-testid="wallet-transactions-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3.5">Type</th><th className="px-5 py-3.5">Amount</th>
              <th className="px-5 py-3.5">Reason</th><th className="px-5 py-3.5">Date</th>
            </tr>
          </thead>
          <tbody>
            {filteredTxns.map((t) => (
              <tr key={t.id} className="border-b border-slate-100 last:border-0">
                <td className="px-5 py-3.5">
                  <span className={`flex items-center gap-1.5 font-bold ${t.type === "credit" ? "text-emerald-600" : "text-red-500"}`}>
                    {t.type === "credit" ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                    {t.type}
                  </span>
                </td>
                <td className="px-5 py-3.5 font-semibold">₹{t.amount.toLocaleString("en-IN")}</td>
                <td className="px-5 py-3.5 text-slate-600">{t.reason}</td>
                <td className="px-5 py-3.5 text-xs text-slate-500">{new Date(t.created_at).toLocaleString("en-IN")}</td>
              </tr>
            ))}
            {!filteredTxns.length && (
              <tr><td colSpan={4} className="px-5 py-12 text-center text-slate-400">No transactions yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
